import createSagaMiddleware from "redux-saga";
import { configureStore, combineReducers } from "@reduxjs/toolkit";

import signUpReducer from "./signUp/reducer";
import suggestedLivestreamsReducer from "./suggestedLivestreams/reducer";
import verificationReducer from "./verification/reducer";
import signInReducer from "./signIn/reducer";
import profileReducer from "./profile/reducer";
import twoFactorsReducer from "./twoFactors/reducer";
import forgotPasswordReducer from "./forgotPassword/reducer";
import addEmailReducer from "./addEmail/reducer";
import addMetamaskReducer from "./addMetamask/reducer";
import changeEmailReducer from "./changeEmail/reducer";
import updatePasswordReducer from "./changePassword/reducer";
import creatorsReducer from "./creators/reducer";
import policySettingReducer from "./policySettings/reducer";
import notificationsSettingsReducer from "./notificationsSettings/reducer";
import addStripeReducer from "./addStripe/reducer";
import addPayPalReducer from "./addPayPal/reducer";
import currencyRatesReducer from "./currencyRates/reducer";
import postsReducer from "./posts/reducer";
import scheduledPostsReducer from "./scheduledPosts/reducer";
import scheduledStreamsReducer from "./scheduledStreams/reducer";
import creatorStreamsReducer from "./creatorStreams/reducer";
import pastStreamsReducer from "./pastStreams/reducer";
import subscriptionsReducer from "./subscriptions/reducer";
import authorsReducer from "./authors/reducer";
import postDetailsReducer from "./postDetails/reducer";
import creatorProfileReducer from "./creatorProfile/reducer";
import subscriptionDetailsReducer from "./subscriptionDetails/reducer";
import reportReducer from "./report/reducer";
import blockListReducer from "./blockList/reducer";
import followersReducer from "./followers/reducer";
import supportersReducer from "./supporters/reducer";
import likesListReducer from "./likesList/reducer";
import promotersReducer from "./promoters/reducer";
import playerReducer from "./player/reducer";
import commentsReducer from "./comments/reducer";
import paymentsReducer from "./payments/reducer";
import createSubscriptionPostsReducer from "./createSubscriptionPost/reducer";
import emailsReducer from "./emails/reducer";
import faqReducer from "./faq/reducer";
import conversationsReducer from "./conversations/reducer";
import notificationReducer from "./notifications/reducer";
import statisticsReducer from "./statistics/reducer";
import cardsReducer from "./paymentCards/reducer";
import cardDetailsReducer from "./cardDetails/reducer";
import postsBySubscriptionReducer from "./postsBySubscription/reducer";
import contentProposalReducer from "./contentProposal/reducer";
import uploadReducer from "./upload/reducer";
import withdrawalReducer from "./widthdrawl/reducer";
import consentFormsReducer from "./consentForms/reducer";
import contentRequestReducer from "./contentRequest/reducer";
import tourReducer from "./tour/reducer";
import suggestionsReducer from "./suggestions/reducer";
import locationBlockerReducer from "./locationBlocker/reducer";
import manualVerificationReducer from "./manualVerification/reducer";
import blockedUserModalReducer from "./blockedUserModal/reducer";
import livestreamsReducer from "./livestreams/reducer";
import livestreamMessagesReducer from "./livestreamMessages/reducer";
import socketReducer from "./socket/reducer";

import { combineSagas } from "@helpers/redux-helpers";

import ContentRequestService from "@services/ContentRequestService";
import StorageService from "../services/StorageService";
import ConsentFormsService from "../services/ConsentFormsService";
import SocialsService from "../services/SocialsService";
import TwoFactorsService from "../services/TwoFactorsService";
import VerificationsService from "../services/VerificationsService";
import SessionsService from "../services/SessionsService";
import UsersService from "../services/UsersService";
import FilesService from "../services/FilesService";
import MoralisService from "../services/MoralisService";
import PolicySettingsService from "../services/PolicySettingsService";
import NotificationsSettingsService from "../services/NotificationsSettingsService";
import StripeService from "../services/StripeService";
import PayPalService from "../services/PayPalService";
import PostsService from "../services/PostsService";
import SubscriptionsService from "../services/SubscriptionsService";
import PaymentsService from "../services/PaymentsService";
import ToasterService from "../services/ToastService";
import LivestreamsService from "../services/LivestreamsService";
import EmailsService from "../services/EmailsService";
import FAQService from "../services/FAQService";
import ConversationsService from "../services/ConversationsService";
import StatisticsService from "../services/StatisticsService";
import SocketService from "../services/SocketService";
import NotificationsService from "../services/NotificationsService";
import MessagesService from "../services/MessagesService";
import CommentsService from "../services/CommentsService";
import CardsService from "../services/CardsService";
import ContentProposalService from "../services/ContentProposalService";
import LocationsListsService from "../services/LocationsListsService";
import LocationBlockerService from "../services/LocationBlockerService";
import TrackingLinksService from "../services/TrackingLinksService";

import watchSignUp from "./signUp/saga";
import { watchForceSignOut, watchSignOut } from "./signOut/saga";
import {
  watchSignIn,
  watchMetamaskSignIn,
  watchGoogleSignIn,
  watchTwitchSignIn,
  watchTwitterSignIn,
} from "./signIn/saga";
import {
  watchResendVerificationLink,
  watchVerifyEmail,
  watchVerifyPasswordCode,
  watchConfirmEmailChange,
} from "./verification/saga";
import {
  watchCompleteTwoFactors,
  watchDisableTwoFactors,
  watchEnableTwoFactors,
  watchGetQrCode,
} from "./twoFactors/saga";
import {
  watchForgotPassword,
  watchRestorePassword,
} from "./forgotPassword/saga";
import { watchAddEmail } from "./addEmail/saga";
import { watchAddMetamask } from "./addMetamask/saga";
import { watchGetCreators } from "./creators/saga";
import {
  watchGetProfile,
  watchUpdateProfile,
  watchUploadAvatar,
  watchDeleteAvatar,
  watchUploadBanner,
  watchDeleteBanner,
  watchDisconnectMetamask,
  watchDisconnectPayoutMethod,
  watchDisconnectPayPal,
  watchDeleteAccount,
  watchSetContentShareTour,
} from "./profile/saga";
import { watchUpdateEmail } from "./changeEmail/saga";
import { watchUpdatePassword } from "./changePassword/saga";
import {
  watchGetPolicySettings,
  watchUpdatePolicySettings,
} from "./policySettings/saga";
import {
  watchGetNotificationsSettings,
  watchUpdateNotificationsSettings,
} from "./notificationsSettings/saga";
import { watchAddStripe, watchGetCountries } from "./addStripe/saga";
import { watchAddPayPal } from "./addPayPal/saga";
import { watchGetRate } from "./currencyRates/saga";
import { watchGetPostDetails } from "./postDetails/saga";
import {
  watchCreatePost,
  watchGetPosts,
  watchGetPortion,
  watchDeletePost,
  watchPinPost,
  watchUnPinPost,
  watchLikePost,
  watchUnlikePost,
  watchGetFeedPosts,
  watchGetFeedPortion,
  watchEditPost,
  watchGetFeedPurchasedPosts,
  watchGetFeedPurchasedPortion,
} from "./posts/saga";
import {
  watchDeleteScheduledPost,
  watchEditScheduledPost,
  watchGetScheduledPortion,
  watchGetScheduledPosts,
} from "./scheduledPosts/saga";
import {
  watchGetcreatorProfile,
  watchBlockCreator,
  watchUnblockCreator,
  watchFollowCreator,
  watchUnfollowCreator,
  watchHideReportNotification,
} from "./creatorProfile/saga";
import { watchReportCreator, watchReportPost } from "./report/saga";
import {
  watchCancelSubscription,
  watchCreateSubscription,
  watchEditSubscription,
  watchGetSubscription,
  watchGetSubscriptionDetails,
  watchGetSubscriptionsByPost,
  watchGetSubscriptionPortion,
  watchGetDeleteMySubscription,
  watchChangePosition,
} from "./subscriptions/saga";
import {
  watchFollowersById,
  watchFollowersByIdPortion,
  watchFollowingsById,
  watchFollowingsByIdPortion,
  watchGetMyFollowers,
  watchGetMyFollowersPortion,
  watchGetMyFollowings,
  watchGetMyFollowingsPortion,
  watchFavoriteFollowing,
  watchUnfavoriteFollowing,
} from "./followers/saga";
import {
  watchGetMySupporters,
  watchGetMySupportersPortion,
  watchSupportersById,
  watchSupportersByIdPortion,
} from "./supporters/saga";
import {
  watchGetMyPromoters,
  watchGetMyPromotersPortion,
  watchPromotersById,
  watchPromotersByIdPortion,
} from "./promoters/saga";
import { watchGetLikesList, watchGetLikesListPortion } from "./likesList/saga";
import {
  watchCreateComment,
  watchDeleteComment,
  watchEditComment,
  watchGetComments,
  watchGetPortionComments,
  watchLikeComment,
  watchUnlikeComment,
} from "./comments/saga";
import {
  watchAddPayoutMethod,
  watchCryptoTx,
  watchGetListDonations,
  watchGetListContentRequests,
  watchGetListSubscription,
  watchGetPaymentSubscription,
  watchGetPaymentSubscriptionPortion,
  watchGetPromotions,
  watchGetAgency,
  watchSubmitTransfer,
  watchSetDefaultCard,
  watchGetAgencyUsers,
} from "./payments/saga";
import { watchWeb3TxChannel } from "./web3/channel";
import {
  watchAttachPosts,
  watchGetCreateSubscriptionPosts,
  watchGetMoreCreateSubscriptionPosts,
} from "./createSubscriptionPost/saga";
import { watchGetUserBlockList } from "./blockList/saga";
import { watchSendsEmail } from "./emails/saga";
import {
  watchGetFAQCategoryItems,
  watchGetFAQCategoryItemsPortion,
  watchGetListFAQCategories,
  watchGetListFAQCategoriesPortion,
} from "./faq/saga";
import {
  watchCsvLinkReceived,
  watchRequestDownloadCsv,
  watchGetCounters,
  watchGetDistributions,
  watchGetIncome,
  watchGetSales,
  watchGetTransactions,
} from "./statistics/saga";
import watchSocket from "./socket/saga";
import {
  watchGetNotifications,
  watchGetNotificationsCount,
  watchGetNotificationsPortion,
  watchHideNotifications,
  watchNewNotificationReceived,
  watchReadNotifications,
} from "./notifications/saga";

import {
  watchBlockedByUser,
  watchCreateConversation,
  watchDeleteMessage,
  watchGetConversationDetails,
  watchGetConversations,
  watchGetMessages,
  watchGetMoreConversations,
  watchGetUnreadCount,
  watchHideConversation,
  watchMessageDeleted,
  watchMoreGetMessages,
  watchMyConversationRead,
  watchNewConversationReceived,
  watchReadConversation,
  watchReceiveMessage,
  watchSendMessage,
  watchUnblockedByUser,
  watchUpdateConversation,
  watchUpdateMessagesCounter,
  wathcEditMessage,
  wathcMessageEdited,
} from "./conversations/saga";
import {
  watchAddCard,
  watchDeleteCard,
  watchGetCards,
} from "./paymentCards/saga";
import { watchGetCardDetails } from "./cardDetails/saga";
import {
  watchGetAllPostsBySubscription,
  watchGetMorePostsBySubscription,
  watchGetPostsBySubscription,
  watchGetSibilingPostsInSubscroption,
} from "./postsBySubscription/saga";
import {
  watchCreateProposal,
  watchGetMoreProposalUsers,
  watchGetProposalUsers,
  watchEditProposal,
  watchGetProposalDetails,
} from "./contentProposal/saga";
import { watchWithdraw } from "./widthdrawl/saga";
import watchConsentFormsSocket, {
  watchAssignForm,
  watchCreateConsentForm,
  watchDeleteConsentForm,
  watchEditConsentFormName,
  watchGetConsentForms,
  watchGetNewSignConsentFormsCount,
  watchGetPortionConsentForms,
  watchGetPortionReceivedForms,
  watchGetReceivedForms,
  watchRejectReceivedForm,
  watchSignReceivedForm,
} from "./consentForms/saga";
import {
  watchGetSuggestions,
  watchGetSuggestionsPortion,
} from "./suggestions/saga";
import {
  watchDeleteLocationBlocker,
  watchGetLocationBlockerLocations,
  watchSetLocationBlocker,
} from "./locationBlocker/saga";
import { watchCreateManualVerification } from "./manualVerification/saga";
import {
  watchAcceptInvite,
  watchBanParticipant,
  watchChangeParticipantRole,
  watchCreateLivestream,
  watchDeleteAllParticipantMessages,
  watchDeleteLivestreamMessage,
  watchEndLivestream,
  watchGetInviteList,
  watchGetLivestreamDetails,
  watchGetLivestreams,
  watchGetMessagesForLivestream,
  watchGetNewMessage,
  watchGetNewTip,
  watchGetParticipants,
  watchGetParticipantsUpdate,
  watchGetTopTippers,
  watchGetUpdatedLivestreamDetails,
  watchHandleBanParticipant,
  watchHandleDeleteAllParticipantMessages,
  watchHandleDeleteMessage,
  watchHandleInviteFromStreamerToViewer,
  watchHandleMuteParticipant,
  watchInviteParticipant,
  watchJoinLivestream,
  watchLeaveLivestream,
  watchLivestreamEnded,
  watchMuteParticipant,
  watchRejectInvite,
  watchReportStream,
  watchRequestInviteToCoStreaming,
  watchSendMessageToLivestream,
  watchUpdateLivestream,
  watchUpdateLivestreamPreview,
  watchUpdateLivestreamGoal,
  watchDeleteLivestreamGoal,
  watchHandleHostAcceptedRequestToCoStreaming,
  watchHandleCoStreamerStartsStream,
  watchStartSpinTheBottle,
  watchEndSpinTheBottle,
  watchSpinTheBottle,
  watchMakeCoStreamerAViewer,
  watchHandleChangeCoStreamerRoleToViewer,
  watchRejectAll,
  watchGetLivestreamsPortion,
} from "./livestreams/saga";
import { watchGetScheduledStreams } from "./scheduledStreams/saga";
import { watchGetCreatorStreams } from "./creatorStreams/saga";
import { watchGetContentRequestMock } from "@redux/contentRequest/saga";
import {
  watchGetSuggestedLivestreams,
  watchGetSuggestedLivestreamsPortion,
} from "@redux/suggestedLivestreams/saga";
import { watchGetPastStreams } from "@redux/pastStreams/saga";
import { watchGetLivestreamMessages } from "@redux/livestreamMessages/saga";

//pls follow ABC order in future reducers implementation
const rootReducer = combineReducers({
  addEmailReducer,
  addMetamaskReducer,
  addPayPalReducer,
  addStripeReducer,
  authorsReducer,
  blockListReducer,
  blockedUserModalReducer,
  cardsReducer,
  cardDetailsReducer,
  changeEmailReducer,
  commentsReducer,
  consentFormsReducer,
  contentProposalReducer,
  contentRequestReducer,
  conversationsReducer,
  creatorProfileReducer,
  creatorStreamsReducer,
  creatorsReducer,
  createSubscriptionPostsReducer,
  currencyRatesReducer,
  emailsReducer,
  faqReducer,
  followersReducer,
  forgotPasswordReducer,
  likesListReducer,
  livestreamsReducer,
  livestreamMessagesReducer,
  locationBlockerReducer,
  manualVerificationReducer,
  notificationsSettingsReducer,
  notificationReducer,
  pastStreamsReducer,
  paymentsReducer,
  playerReducer,
  policySettingReducer,
  postsBySubscriptionReducer,
  postsReducer,
  postDetailsReducer,
  profileReducer,
  promotersReducer,
  reportReducer,
  scheduledPostsReducer,
  scheduledStreamsReducer,
  signInReducer,
  signUpReducer,
  socketReducer,
  statisticsReducer,
  subscriptionsReducer,
  subscriptionDetailsReducer,
  suggestedLivestreamsReducer,
  suggestionsReducer,
  supportersReducer,
  tourReducer,
  twoFactorsReducer,
  updatePasswordReducer,
  uploadReducer,
  verificationReducer,
  withdrawalReducer,
});

const rootSaga = combineSagas([
  watchSignUp,
  watchResendVerificationLink,
  watchVerifyEmail,
  watchSignIn,
  watchMetamaskSignIn,
  watchTwitchSignIn,
  watchTwitterSignIn,
  watchGoogleSignIn,
  watchGetQrCode,
  watchEnableTwoFactors,
  watchDisableTwoFactors,
  watchCompleteTwoFactors,
  watchForgotPassword,
  watchRestorePassword,
  watchAddEmail,
  watchAddMetamask,
  watchGetCreators,
  watchSignOut,
  watchForceSignOut,
  watchGetProfile,
  watchUpdateProfile,
  watchUploadAvatar,
  watchDeleteAvatar,
  watchUploadBanner,
  watchDeleteBanner,
  watchUpdateEmail,
  watchUpdatePassword,
  watchGetPolicySettings,
  watchUpdatePolicySettings,
  watchGetNotificationsSettings,
  watchUpdateNotificationsSettings,
  watchAddStripe,
  watchAddPayPal,
  watchGetRate,
  watchCreatePost,
  watchVerifyPasswordCode,
  watchConfirmEmailChange,
  watchGetPosts,
  watchGetPortion,
  watchGetPostDetails,
  watchDeletePost,
  watchPinPost,
  watchUnPinPost,
  watchLikePost,
  watchUnlikePost,
  watchGetcreatorProfile,
  watchBlockCreator,
  watchUnblockCreator,
  watchReportCreator,
  watchReportPost,
  watchCreateSubscription,
  watchGetSubscription,
  watchEditSubscription,
  watchGetMyFollowers,
  watchGetMyFollowersPortion,
  watchGetMyFollowings,
  watchGetMyFollowingsPortion,
  watchCreateComment,
  watchGetComments,
  watchGetPortionComments,
  watchGetFeedPosts,
  watchGetFeedPurchasedPosts,
  watchGetFeedPortion,
  watchGetFeedPurchasedPortion,
  watchGetPaymentSubscription,
  watchWithdraw,
  watchGetPaymentSubscriptionPortion,
  watchFollowCreator,
  watchUnfollowCreator,
  watchSubmitTransfer,
  watchGetListSubscription,
  watchGetListDonations,
  watchGetListContentRequests,
  watchGetSubscriptionDetails,
  watchCancelSubscription,
  watchWeb3TxChannel,
  watchCryptoTx,
  watchGetSubscriptionsByPost,
  watchFollowersById,
  watchFollowersByIdPortion,
  watchFollowingsById,
  watchFollowingsByIdPortion,
  watchGetMySupporters,
  watchGetMySupportersPortion,
  watchSupportersById,
  watchSupportersByIdPortion,
  watchGetSuggestions,
  watchGetSuggestedLivestreams,
  watchGetSuggestedLivestreamsPortion,
  watchGetSuggestionsPortion,
  watchGetMyPromoters,
  watchGetMyPromotersPortion,
  watchPromotersById,
  watchPromotersByIdPortion,
  watchGetLikesList,
  watchGetLikesListPortion,
  watchGetSubscriptionPortion,
  watchHideReportNotification,
  watchGetCreateSubscriptionPosts,
  watchGetMoreCreateSubscriptionPosts,
  watchAttachPosts,
  watchGetUserBlockList,
  watchGetDeleteMySubscription,
  watchSendsEmail,
  watchChangePosition,
  watchSocket,
  watchConsentFormsSocket,
  watchGetListFAQCategories,
  watchGetListFAQCategoriesPortion,
  watchGetFAQCategoryItems,
  watchGetFAQCategoryItemsPortion,
  watchGetCounters,
  watchGetDistributions,
  watchGetSales,
  watchGetIncome,
  watchGetTransactions,
  watchGetNotifications,
  watchGetNotificationsPortion,
  watchGetNotificationsCount,
  watchHideNotifications,
  watchReadNotifications,
  watchGetConversations,
  watchGetMoreConversations,
  watchGetMessages,
  watchMoreGetMessages,
  watchHideConversation,
  watchReceiveMessage,
  watchUpdateConversation,
  watchUpdateMessagesCounter,
  watchDeleteMessage,
  watchBlockedByUser,
  watchUnblockedByUser,
  watchSendMessage,
  watchCreateConversation,
  watchGetConversationDetails,
  watchReadConversation,
  watchMyConversationRead,
  watchEditPost,
  watchDeleteScheduledPost,
  watchEditScheduledPost,
  watchGetScheduledPortion,
  watchGetScheduledPosts,
  watchMessageDeleted,
  watchGetUnreadCount,
  watchDisconnectMetamask,
  watchDisconnectPayoutMethod,
  watchDisconnectPayPal,
  watchNewNotificationReceived,
  watchRequestDownloadCsv,
  watchCsvLinkReceived,
  watchDeleteAccount,
  watchSetContentShareTour,
  watchGetPromotions,
  watchGetAgency,
  watchLikeComment,
  watchUnlikeComment,
  watchDeleteComment,
  watchEditComment,
  wathcEditMessage,
  wathcMessageEdited,
  watchNewConversationReceived,
  watchAddCard,
  watchGetCards,
  watchDeleteCard,
  watchGetCardDetails,
  watchGetCountries,
  watchGetAllPostsBySubscription,
  watchGetPostsBySubscription,
  watchGetMorePostsBySubscription,
  watchGetSibilingPostsInSubscroption,
  watchGetProposalUsers,
  watchGetMoreProposalUsers,
  watchCreateProposal,
  watchEditProposal,
  watchGetProposalDetails,
  watchGetContentRequestMock,
  watchFavoriteFollowing,
  watchUnfavoriteFollowing,
  watchCreateConsentForm,
  watchGetConsentForms,
  watchGetPortionConsentForms,
  watchEditConsentFormName,
  watchDeleteConsentForm,
  watchGetPortionReceivedForms,
  watchGetReceivedForms,
  watchRejectReceivedForm,
  watchSignReceivedForm,
  watchAssignForm,
  watchAddPayoutMethod,
  watchSetDefaultCard,
  watchGetAgencyUsers,
  watchGetNewSignConsentFormsCount,
  watchGetLocationBlockerLocations,
  watchDeleteLocationBlocker,
  watchSetLocationBlocker,
  watchCreateManualVerification,
  watchCreateLivestream,
  watchEndLivestream,
  watchGetLivestreams,
  watchGetLivestreamsPortion,
  watchJoinLivestream,
  watchBanParticipant,
  watchReportStream,
  watchMuteParticipant,
  watchLeaveLivestream,
  watchUpdateLivestream,
  watchSendMessageToLivestream,
  watchGetMessagesForLivestream,
  watchGetParticipants,
  watchGetTopTippers,
  watchGetNewMessage,
  watchGetNewTip,
  watchGetLivestreamDetails,
  watchGetUpdatedLivestreamDetails,
  watchGetParticipantsUpdate,
  watchHandleDeleteMessage,
  watchHandleChangeCoStreamerRoleToViewer,
  watchHandleBanParticipant,
  watchHandleMuteParticipant,
  watchLivestreamEnded,
  watchUpdateLivestreamPreview,
  watchGetInviteList,
  watchHandleInviteFromStreamerToViewer,
  watchHandleHostAcceptedRequestToCoStreaming,
  watchHandleCoStreamerStartsStream,
  watchInviteParticipant,
  watchAcceptInvite,
  watchRejectInvite,
  watchRejectAll,
  watchRequestInviteToCoStreaming,
  watchMakeCoStreamerAViewer,
  watchChangeParticipantRole,
  watchDeleteLivestreamMessage,
  watchDeleteAllParticipantMessages,
  watchHandleDeleteAllParticipantMessages,
  watchUpdateLivestreamGoal,
  watchDeleteLivestreamGoal,
  watchStartSpinTheBottle,
  watchEndSpinTheBottle,
  watchSpinTheBottle,
  watchGetScheduledStreams,
  watchGetPastStreams,
  watchGetCreatorStreams,
  watchGetLivestreamMessages,
]);

const sagaMiddleware = createSagaMiddleware({
  context: {
    services: {
      StorageService,
      UsersService,
      VerificationsService,
      SessionsService,
      SocialsService,
      TwoFactorsService,
      FilesService,
      MoralisService,
      PolicySettingsService,
      NotificationsSettingsService,
      StripeService,
      PayPalService,
      PostsService,
      SubscriptionsService,
      PaymentsService,
      ToasterService,
      EmailsService,
      FAQService,
      StatisticsService,
      SocketService,
      NotificationsService,
      ConversationsService,
      ContentRequestService,
      MessagesService,
      CommentsService,
      CardsService,
      ContentProposalService,
      ConsentFormsService,
      LocationsListsService,
      LocationBlockerService,
      TrackingLinksService,
      LivestreamsService,
    },
  },
});

const middlewares = [sagaMiddleware];

const store = configureStore({
  reducer: rootReducer,
  devTools: import.meta.env.DEV,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ thunk: false, serializableCheck: false }).concat(
      ...middlewares,
    ),
});

sagaMiddleware.run(rootSaga);

export default store;
